import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { useLanguage } from '../../contexts/LanguageContext';
import { MessageCircle, Users, TrendingUp, Cpu, BarChart3, PieChart, Brain } from 'lucide-react';

const AIUsage = () => {
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const { t } = useLanguage();

    useEffect(() => {
        loadUsageData();
    }, [period]);

    const loadUsageData = async () => {
        try {
            setLoading(true);
            const response = await analyticsService.getAIMonitoringStats(period);
            if (response.success) {
                setUsageData(response.data);
            }
        } catch (error) {
            console.error(t('loadStatsError'), error);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ icon, title, value, subtitle, color = 'primary' }) => (
        <div className={`stat-card stat-card--${color}`}>
            <div className="stat-icon">{icon}</div>
            <div className="stat-content">
                <h3>{value}</h3>
                <p>{title}</p>
                {subtitle && <div className="stat-subtitle">{subtitle}</div>}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <div>{t('loading')}</div>
            </div>
        );
    }

    if (!usageData) {
        return <div className="no-data">{t('noStatsAvailable')}</div>;
    }

    const generalStats = usageData.generalStatistics;
    const planStats = usageData.planStatistics;
    const modelStats = usageData.modelStatistics;
    const topUsers = usageData.topUsers;

    return (
        <div className="ai-usage">
            {/* Period Selector */}
            <div className="period-selector">
                <div className="period-buttons">
                    {['today', 'week', 'month', 'year'].map(p => (
                        <button
                            key={p}
                            className={`period-button ${period === p ? 'active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {t(p)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Metrics Grid */}
            <div className="metrics-grid">
                <StatCard
                    icon={<Brain size={24} />}
                    title={t('totalConversations')}
                    value={generalStats?.total_messages?.toLocaleString() || '0'}
                    subtitle={`${generalStats?.active_users || 0} ${t('activeAIUsers')}`}
                    color="purple"
                />
                <StatCard
                    icon={<MessageCircle size={24} />}
                    title={t('totalTokens')}
                    value={(generalStats?.total_tokens || 0).toLocaleString()}
                    color="blue"
                />
                <StatCard
                    icon={<Users size={24} />}
                    title={t('activeAIUsers')}
                    value={generalStats?.active_users?.toLocaleString() || '0'}
                    color="green"
                />
                <StatCard
                    icon={<Cpu size={24} />}
                    title={t('avgTokensPerMessage')}
                    value={(generalStats?.avg_tokens_per_message || 0).toFixed(0)}
                    subtitle={`${generalStats?.fallback_messages || 0} ${t('fallbackMessages')}`}
                    color="orange"
                />
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <BarChart3 size={20} />
                        <h3>{t('usageTrend')}</h3>
                    </div>
                    <div className="chart-placeholder">
                        <p>🚀 {t('chartPlaceholder')}</p>
                        <div className="chart-legend">
                            <div className="legend-item">
                                <span className="legend-color purple"></span>
                                <span>{t('messages')}</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-color blue"></span>
                                <span>{t('tokens')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <PieChart size={20} />
                        <h3>{t('modelDistribution')}</h3>
                    </div>
                    <div className="chart-placeholder">
                        <div className="model-distribution">
                            {modelStats?.map(model => (
                                <div key={model.model_used} className="model-item">
                                    <span className="model-label">{model.model_used}</span>
                                    <span className="model-value">{model.usage_count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="chart-card full-width">
                    <div className="chart-header">
                        <TrendingUp size={20} />
                        <h3>{t('topUsers')}</h3>
                    </div>
                    <div className="top-users-list">
                        {topUsers?.map((user, index) => (
                            <div key={user.id} className="user-item">
                                <div className="user-rank">#{index + 1}</div>
                                <div className="user-info">
                                    <div className="user-name">{user.name}</div>
                                    <div className="user-email">{user.email}</div>
                                </div>
                                <div className="user-stats">
                                    <span className="user-messages">{user.message_count} {t('messages')}</span>
                                    <span className="user-tokens">{user.tokens_used?.toLocaleString()} {t('tokens')}</span>
                                </div>
                                <div className={`user-plan ${user.plan}`}>{t(user.plan)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIUsage;